import { DeviceType, FilterFn } from '@renderer/types/utils'
import { Webcam } from '../../lib/webcam/webcam'
import { imageUtils } from '../imageUtils'
import { Camera } from '@renderer/lib/web-gphoto2/camera'
import { useSettingsStore } from '@renderer/stores/settingsStore'
import { useCameraStore } from '@renderer/stores/cameraStore'
import cogoToast from '@successtar/cogo-toast'

interface USBCameraParams {
  productId?: number | undefined
  vendorId?: number | undefined
}

interface ConnectResult {
  deviceInfo?: {
    productName?: string
    manufacturerName?: string
    serialNumber?: string
    vendorId?: number
    productId?: number
  }
  type: DeviceType
}

type CameraKey = 'primary' | 'secondary'

const LIVEVIEW_BUSY_RETRY_COUNT = 5
const LIVEVIEW_BUSY_RETRY_DELAY_MS = 600

/** USB productId (decimal) → min ms to wait after physical shutter before liveview fetch */
const POST_SHUTTER_LIVEVIEW_DELAY_MS_BY_PRODUCT_ID: Readonly<Record<number, number>> = {
  0x0428: 800 // Nikon D7000 (VID 04B0)
}

const lastShutterCompletedAt: Record<CameraKey, number> = { primary: 0, secondary: 0 }

function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms))
}

/** Transient gphoto2 / PTP "busy" conditions — must not clear selectedCamera in capturePreview. */
function isGphotoCameraBusyError(err: unknown): boolean {
  const s = err instanceof Error ? err.message : String(err)
  return (
    /2501288/i.test(s) ||
    /2873416/i.test(s) ||
    /camera\s*busy/i.test(s) ||
    /device\s*busy/i.test(s) ||
    /ptp.*busy/i.test(s) ||
    /\b0x2019\b/i.test(s) ||
    /full-press\s*failed/i.test(s)
  )
}

/**
 * Transient USB / PTP I/O conditions on a tethered DSLR that recover on their own.
 * These are NOT a disconnected camera — a single such error must not tear down a healthy
 * camera session (the old code nulled selectedCamera + camera on ANY non-"busy" error,
 * so one liveview hiccup stranded a paid customer mid-capture). Treated like "busy":
 * skip the frame, keep the camera, and only give up after sustained consecutive failures.
 */
function isTransientCameraReadError(err: unknown): boolean {
  const s = err instanceof Error ? err.message : String(err)
  return (
    /libusb/i.test(s) ||
    /\btimed?\s*out\b/i.test(s) ||
    /timeout/i.test(s) ||
    /could\s*not\s*claim/i.test(s) ||
    /\bI\/O\b/i.test(s) ||
    /\bio\s*error\b/i.test(s) ||
    /\bEIO\b/.test(s) ||
    /no\s*response/i.test(s) ||
    /\btransfer\b/i.test(s) ||
    /\bpipe\b/i.test(s) ||
    /\bstall\b/i.test(s) ||
    /\b0x200[0-9a-f]\b/i.test(s) // PTP general/transient response codes (0x2000–0x200F)
  )
}

/**
 * Only declare a tethered camera "gone" (null the store + clear selectedCamera) after this
 * many CONSECUTIVE preview-read failures. A genuine unplug fails every read and crosses the
 * threshold quickly; a transient blip resets on the next good frame.
 */
const MAX_CONSECUTIVE_PREVIEW_FAILURES = 4

function parseUsbProductIdFromSelected(selected?: string): number | undefined {
  if (selected == null || selected === 'detect') return undefined
  const n = Number(selected)
  return Number.isFinite(n) ? n : undefined
}

function getPostShutterLiveviewDelayMs(
  cameraKey: CameraKey,
  settings: ReturnType<typeof useSettingsStore.getState>
): number {
  const cam =
    cameraKey === 'primary' ? settings.cameraSettings : settings.secondaryCameraSettings
  const override = cam.postShutterLiveviewDelayMs
  if (typeof override === 'number' && override >= 0) {
    return override
  }
  const selected =
    cameraKey === 'primary' ? settings.selectedCamera : settings.selectedSecondaryCamera
  const productId = parseUsbProductIdFromSelected(selected)
  if (productId !== undefined && productId in POST_SHUTTER_LIVEVIEW_DELAY_MS_BY_PRODUCT_ID) {
    return POST_SHUTTER_LIVEVIEW_DELAY_MS_BY_PRODUCT_ID[productId]
  }
  return 0
}

async function applyPostShutterLiveviewDelayIfNeeded(
  cameraKey: CameraKey,
  useShutterCapture: boolean,
  postShutterDelayMs: number
): Promise<void> {
  if (!useShutterCapture || postShutterDelayMs <= 0) return
  const last = lastShutterCompletedAt[cameraKey]
  if (last <= 0) return
  const remaining = postShutterDelayMs - (Date.now() - last)
  if (remaining > 0) {
    await sleep(remaining)
  }
}

async function captureDslrPreviewAsBlobWithPostShutterWaitAndRetry(
  camera: Camera,
  cameraKey: CameraKey
): Promise<Blob | undefined> {
  const settings = useSettingsStore.getState()
  const isPrimary = cameraKey === 'primary'
  const useShutterCapture = isPrimary
    ? settings.cameraSettings.useShutterCapture
    : settings.secondaryCameraSettings.useShutterCapture
  const postShutterDelayMs = getPostShutterLiveviewDelayMs(cameraKey, settings)
  await applyPostShutterLiveviewDelayIfNeeded(cameraKey, useShutterCapture, postShutterDelayMs)

  for (let i = 0; i < LIVEVIEW_BUSY_RETRY_COUNT; i++) {
    try {
      const blob = await camera.capturePreviewAsBlob()
      return blob ?? undefined
    } catch (err) {
      if (isGphotoCameraBusyError(err) && i < LIVEVIEW_BUSY_RETRY_COUNT - 1) {
        console.warn(
          `Liveview busy (attempt ${i + 1}/${LIVEVIEW_BUSY_RETRY_COUNT}), retrying in ${LIVEVIEW_BUSY_RETRY_DELAY_MS}ms...`
        )
        await sleep(LIVEVIEW_BUSY_RETRY_DELAY_MS)
        continue
      }
      if (isGphotoCameraBusyError(err)) {
        console.warn('Liveview still busy after retries; skipping frame', err)
        return undefined
      }
      throw err
    }
  }
  return undefined
}

function markLastShutterCompleted(cameraKey: CameraKey): void {
  lastShutterCompletedAt[cameraKey] = Date.now()
}

// Add new interface for image quality detection
interface ImageQualityResult {
  isResponding: boolean
  isImageValid: boolean
  qualityIssue?: 'dark' | 'black' | 'noise' | 'none'
}

class UnifiedCameraUtils {
  private _isDisconnected: Record<CameraKey, boolean> = { primary: false, secondary: false }
  private _configMutationDepth: Record<CameraKey, number> = { primary: 0, secondary: 0 }
  /** Consecutive capturePreview failures per camera; reset on any successful read. */
  private _consecutivePreviewFailures: Record<CameraKey, number> = { primary: 0, secondary: 0 }
  /**
   * Depth of in-flight captureImage/captureImageAsFile calls per camera. While > 0 the
   * device is actively shooting; the View health monitor must not issue a competing liveview
   * read (USB/PTP is single-access — contention is what *causes* the transient "busy"/I/O
   * errors) nor tear the camera down mid-shot. Counter (not boolean) to survive nesting
   * (captureImage -> captureImageAsFile).
   */
  private _captureDepth: Record<CameraKey, number> = { primary: 0, secondary: 0 }

  async getDevices(): Promise<{ webcams: MediaDeviceInfo[]; pairedCameras: USBDevice[] }> {
    try {
      const webcams = await Webcam.getDevices()
      const pairedCameras = await navigator.usb.getDevices()
      return { webcams, pairedCameras }
    } catch (err) {
      console.error('Error getting devices:', err)
      return { webcams: [], pairedCameras: [] }
    }
  }

  async tryToConnect(
    deviceType: DeviceType,
    params: string | USBCameraParams,
    cameraKey: CameraKey = 'primary'
  ): Promise<ConnectResult> {
    const store = useCameraStore.getState()
    const isPrimary = cameraKey === 'primary'
    // Fresh connection attempt starts with a clean preview-failure streak.
    this._consecutivePreviewFailures[cameraKey] = 0

    try {
      if (deviceType === 'webcam') {
        const currentWebcam = isPrimary ? store.webcam : store.secondaryWebcam
        if (currentWebcam) {
          await currentWebcam.disconnect()
          if (isPrimary) {
            store.setWebcam(null)
          } else {
            store.setSecondaryWebcam(null)
          }
        }

        const webcamId = params as string
        const webcam = new Webcam()
        await webcam.connect(webcamId)
        if (isPrimary) {
          store.setWebcam(webcam)
          store.setDeviceType(deviceType)
        } else {
          store.setSecondaryWebcam(webcam)
          store.setSecondaryDeviceType(deviceType)
        }
        this._isDisconnected[cameraKey] = false
        return { type: deviceType }
      } else if (deviceType === 'camera') {
        const currentWebcam = isPrimary ? store.webcam : store.secondaryWebcam
        if (currentWebcam) {
          await currentWebcam.disconnect()
          if (isPrimary) {
            store.setWebcam(null)
          } else {
            store.setSecondaryWebcam(null)
          }
        }

        const currentCamera = isPrimary ? store.camera : store.secondaryCamera
        if (!currentCamera) {
          const camera = new Camera()
          const filters: USBDeviceFilter[] = []
          filters.push({ classCode: 6, subclassCode: 1 })

          if (typeof params !== 'string' && params.vendorId && params.productId) {
            filters.push({ vendorId: params.vendorId, productId: params.productId })
          }

          if (window.electron && window.api.setUsbDeviceFilters) {
            window.api.setUsbDeviceFilters(filters)
          }

          console.log(`Requesting ${cameraKey} device with filters: `, filters)
          const usbDevice = await navigator.usb.requestDevice({ filters })

          console.log(`${cameraKey} device retrieved: `, usbDevice)

          await camera.connect()
          if (isPrimary) {
            store.setCamera(camera)
            store.setDeviceType(deviceType)
          } else {
            store.setSecondaryCamera(camera)
            store.setSecondaryDeviceType(deviceType)
          }

          this._isDisconnected[cameraKey] = false
          return {
            type: deviceType,
            deviceInfo: {
              productName: usbDevice.productName,
              manufacturerName: usbDevice.manufacturerName,
              serialNumber: usbDevice.serialNumber,
              vendorId: usbDevice.vendorId,
              productId: usbDevice.productId
            }
          }
        }
        this._isDisconnected[cameraKey] = false
        return { type: deviceType }
      }
      throw new Error('Invalid device type')
    } catch (error: unknown) {
      if (isPrimary) {
        useSettingsStore.getState().setSelectedCamera(undefined)
      } else {
        useSettingsStore.getState().setSelectedSecondaryCamera(undefined)
      }
      await this.disconnect(cameraKey)
      const errorMessage =
        error instanceof Error
          ? error.message
          : typeof error === 'string'
            ? error
            : 'Error tidak diketahui'

      if (
        errorMessage.includes('LIBUSB_ERROR_ACCESS') ||
        errorMessage.includes('Cannot use deleted val')
      ) {
        throw new Error(
          'Harap lepas dan sambungkan kembali kabel USB kamera Anda, lalu refresh aplikasi dan coba lagi'
        )
      } else if (errorMessage.includes('memory access out of bounds')) {
        throw new Error('Terlalu banyak percobaan gagal. Memuat ulang...')
      } else {
        throw new Error(`Gagal menyambungkan kamera: ${errorMessage}`)
      }
    }
  }

  /**
   * Detects if an image is too dark or black
   * @param blob - The image blob to analyze
   * @returns Object indicating if image is valid and what quality issues exist
   */
  private async detectImageQuality(
    blob: Blob
  ): Promise<{ isValid: boolean; issue?: 'dark' | 'black' | 'noise' | 'none' }> {
    try {
      // Create canvas to analyze image
      const canvas = document.createElement('canvas')
      const ctx = canvas.getContext('2d')
      if (!ctx) return { isValid: false, issue: 'noise' }

      const img = new Image()
      const url = URL.createObjectURL(blob)

      return new Promise((resolve) => {
        img.onload = (): void => {
          URL.revokeObjectURL(url)

          // Set canvas size to image size
          canvas.width = img.width
          canvas.height = img.height

          // Draw image to canvas
          ctx.drawImage(img, 0, 0)

          // Get image data for analysis
          const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height)
          const data = imageData.data

          let totalBrightness = 0
          let darkPixels = 0
          let blackPixels = 0
          const totalPixels = data.length / 4

          // Analyze each pixel
          for (let i = 0; i < data.length; i += 4) {
            const r = data[i]
            const g = data[i + 1]
            const b = data[i + 2]

            // Calculate brightness (using luminance formula)
            const brightness = 0.299 * r + 0.587 * g + 0.114 * b
            totalBrightness += brightness

            // Count dark pixels (brightness < 30)
            if (brightness < 30) {
              darkPixels++
            }

            // Count black pixels (brightness < 10)
            if (brightness < 10) {
              blackPixels++
            }
          }

          const avgBrightness = totalBrightness / totalPixels
          const darkPercentage = (darkPixels / totalPixels) * 100
          const blackPercentage = (blackPixels / totalPixels) * 100

          // Determine if image is problematic
          let issue: 'dark' | 'black' | 'noise' | 'none' = 'none'
          let isValid = true

          if (blackPercentage > 80) {
            issue = 'black'
            isValid = false
          } else if (darkPercentage > 70 || avgBrightness < 40) {
            issue = 'dark'
            isValid = false
          } else if (avgBrightness < 20) {
            issue = 'dark'
            isValid = false
          }

          resolve({ isValid, issue })
        }

        img.onerror = (): void => {
          URL.revokeObjectURL(url)
          resolve({ isValid: false, issue: 'noise' })
        }

        img.src = url
      })
    } catch (error) {
      console.error('Error analyzing image quality:', error)
      return { isValid: false, issue: 'noise' }
    }
  }

  async isDeviceResponding(cameraKey: CameraKey): Promise<ImageQualityResult> {
    if (this._isDisconnected[cameraKey]) {
      return { isResponding: false, isImageValid: false, qualityIssue: 'none' }
    }

    // A capture is actively running on this device. Don't issue a competing liveview read
    // (it would fight the shutter/preview over the single USB connection and manufacture the
    // very transient errors we're trying to avoid). Report healthy — an in-flight capture is
    // proof the camera is alive.
    if (this._captureDepth[cameraKey] > 0) {
      return { isResponding: true, isImageValid: true, qualityIssue: 'none' }
    }

    const store = useCameraStore.getState()
    const isPrimary = cameraKey === 'primary'
    const deviceType = isPrimary ? store.deviceType : store.secondaryDeviceType
    const webcam = isPrimary ? store.webcam : store.secondaryWebcam
    const camera = isPrimary ? store.camera : store.secondaryCamera

    try {
      if (deviceType === 'webcam' && webcam) {
        const blob = await webcam.capturePreviewAsBlob()
        if (!blob) {
          return { isResponding: false, isImageValid: false, qualityIssue: 'none' }
        }

        // Analyze image quality
        const qualityResult = await this.detectImageQuality(blob)
        return {
          isResponding: true,
          isImageValid: qualityResult.isValid,
          qualityIssue: qualityResult.issue
        }
      } else if (deviceType === 'camera' && camera) {
        const blob = await camera.capturePreviewAsBlob()
        if (!blob) {
          return { isResponding: false, isImageValid: false, qualityIssue: 'none' }
        }

        // Analyze image quality
        const qualityResult = await this.detectImageQuality(blob)
        return {
          isResponding: true,
          isImageValid: qualityResult.isValid,
          qualityIssue: qualityResult.issue
        }
      }

      return { isResponding: false, isImageValid: false, qualityIssue: 'none' }
    } catch {
      return { isResponding: false, isImageValid: false, qualityIssue: 'none' }
    }
  }

  async getConfig(cameraKey: CameraKey): Promise<unknown | undefined> {
    if (this._isDisconnected[cameraKey]) return undefined
    const store = useCameraStore.getState()
    const isPrimary = cameraKey === 'primary'
    const deviceType = isPrimary ? store.deviceType : store.secondaryDeviceType
    const webcam = isPrimary ? store.webcam : store.secondaryWebcam
    const camera = isPrimary ? store.camera : store.secondaryCamera

    if (deviceType === 'webcam' && webcam) {
      return webcam.getConfig()
    } else if (deviceType === 'camera' && camera) {
      return camera.getConfig()
    }
    return undefined
  }

  async setConfigValue(
    cameraKey: CameraKey,
    name: string,
    value: string | number | boolean
  ): Promise<unknown | undefined> {
    if (this._isDisconnected[cameraKey]) return undefined
    const store = useCameraStore.getState()
    const isPrimary = cameraKey === 'primary'
    const deviceType = isPrimary ? store.deviceType : store.secondaryDeviceType
    const camera = isPrimary ? store.camera : store.secondaryCamera

    if (deviceType === 'webcam') {
      return undefined // Webcam doesn't support config changes
    } else if (deviceType === 'camera' && camera) {
      this._configMutationDepth[cameraKey] += 1
      try {
        return await camera.setConfigValue(name, value)
      } finally {
        // Give camera firmware a brief settle window after config write before liveview reads resume.
        await sleep(120)
        this._configMutationDepth[cameraKey] = Math.max(0, this._configMutationDepth[cameraKey] - 1)
      }
    }
    return undefined
  }

  capturePreview = async (
    cameraKey: CameraKey,
    options: {
      maxWidth?: number
      maxHeight?: number
      quality?: number
      rotation?: number // Allow passing rotation explicitly
      mirror?: boolean // Allow passing mirror explicitly
    } = {
      maxWidth:
        cameraKey === 'primary'
          ? useSettingsStore.getState().cameraSettings.previewQuality?.maxWidth
          : useSettingsStore.getState().secondaryCameraSettings.previewQuality?.maxWidth,
      maxHeight:
        cameraKey === 'primary'
          ? useSettingsStore.getState().cameraSettings.previewQuality?.maxHeight
          : useSettingsStore.getState().secondaryCameraSettings.previewQuality?.maxHeight,
      quality:
        cameraKey === 'primary'
          ? useSettingsStore.getState().cameraSettings.previewQuality?.quality
          : useSettingsStore.getState().secondaryCameraSettings.previewQuality?.quality
    }
  ): Promise<Blob | undefined> => {
    if (this._isDisconnected[cameraKey]) return undefined
    const store = useCameraStore.getState()
    const isPrimary = cameraKey === 'primary'
    const deviceType = isPrimary ? store.deviceType : store.secondaryDeviceType
    const webcam = isPrimary ? store.webcam : store.secondaryWebcam
    const camera = isPrimary ? store.camera : store.secondaryCamera

    // Get previewRotation and mirror settings - prioritize explicit options over store.
    // Preview.tsx always passes {rotation: 0, mirror: false} and handles transforms via CSS;
    // other callers (e.g. FrameBasedCapture canvas snapshot) rely on blob-level transforms.
    const settings = useSettingsStore.getState()
    const rotation =
      options?.rotation !== undefined
        ? options.rotation
        : isPrimary
          ? settings.cameraSettings.previewRotation
          : settings.secondaryCameraSettings.previewRotation
    const mirror =
      options?.mirror !== undefined
        ? options.mirror
        : isPrimary
          ? settings.cameraSettings.mirror
          : settings.secondaryCameraSettings.mirror

    try {
      let rawBlob: Blob | undefined
      if (deviceType === 'webcam' && webcam) {
        rawBlob = await webcam.capturePreviewAsBlob(
          options?.maxWidth,
          options?.maxHeight,
          options?.quality
        )
      } else if (deviceType === 'camera' && camera) {
        // Avoid racing liveview preview reads with in-flight camera config writes.
        if (this._configMutationDepth[cameraKey] > 0) {
          let waitCount = 0
          while (this._configMutationDepth[cameraKey] > 0 && waitCount < 20) {
            await sleep(50)
            waitCount += 1
          }
        }
        rawBlob = await captureDslrPreviewAsBlobWithPostShutterWaitAndRetry(camera, cameraKey)
      }

      // Only apply expensive image processing if transforms are actually needed
      if (!rawBlob) return undefined

      // Got a real frame — the camera is alive; clear any prior transient-failure streak.
      this._consecutivePreviewFailures[cameraKey] = 0

      // Apply previewRotation/mirror only if needed (skipped when Preview.tsx passes rotation:0, mirror:false)
      if (rotation !== 0 || mirror) {
        rawBlob = await imageUtils.applyRotationMirroring(rawBlob, rotation, mirror)
      }

      return rawBlob
    } catch (error) {
      if (isGphotoCameraBusyError(error)) {
        console.warn('Preview capture failed (camera busy):', error)
        return undefined
      }

      // Transient USB/PTP I/O (and other non-"busy") errors: tolerate up to a few
      // CONSECUTIVE failures before tearing down the camera. The previous code nulled the
      // camera + cleared selectedCamera on the VERY FIRST such error, which killed a healthy
      // tethered camera on a momentary hiccup and stranded paid customers mid-capture
      // ("No image data captured" / "Camera hardware failure"). A real unplug fails every
      // read and crosses the threshold within a few ticks; a transient blip resets on the
      // next good frame (above).
      this._consecutivePreviewFailures[cameraKey] += 1
      const failures = this._consecutivePreviewFailures[cameraKey]
      const transient = isTransientCameraReadError(error)

      if (failures < MAX_CONSECUTIVE_PREVIEW_FAILURES) {
        console.warn(
          `Preview capture failed (${transient ? 'transient' : 'unclassified'} ${failures}/${MAX_CONSECUTIVE_PREVIEW_FAILURES}); keeping camera:`,
          error
        )
        return undefined
      }

      console.error(
        `Preview capture failed ${failures}x consecutively; treating camera as disconnected:`,
        error
      )
      this._consecutivePreviewFailures[cameraKey] = 0
      if (isPrimary) {
        useSettingsStore.getState().setSelectedCamera(undefined)
        useCameraStore.getState().setCamera(null)
      } else {
        useSettingsStore.getState().setSelectedSecondaryCamera(undefined)
        useCameraStore.getState().setSecondaryCamera(null)
      }
      return undefined
    }
  }

  /**
   * QR-optimized capture that bypasses all transformations for accurate QR code detection
   * This method returns raw, unprocessed images from both DSLR and webcam
   * Optimized for performance with balanced quality for QR detection
   */
  capturePreviewForQR = async (cameraKey: CameraKey): Promise<Blob | undefined> => {
    if (this._isDisconnected[cameraKey]) return undefined
    const store = useCameraStore.getState()
    const isPrimary = cameraKey === 'primary'
    const deviceType = isPrimary ? store.deviceType : store.secondaryDeviceType
    const webcam = isPrimary ? store.webcam : store.secondaryWebcam
    const camera = isPrimary ? store.camera : store.secondaryCamera

    try {
      let rawBlob: Blob | undefined

      if (deviceType === 'webcam' && webcam) {
        // For webcam: Optimized resolution and quality for QR detection
        // Smaller size for faster processing while maintaining QR readability
        rawBlob = await webcam.capturePreviewAsBlob(
          1280, // Reduced from 1920 - still HD enough for QR codes
          720, // Reduced from 1080 - faster processing
          0.9 // Slight compression for faster transfer, still high quality
        )
      } else if (deviceType === 'camera' && camera) {
        // For DSLR: Get raw preview without any processing
        // DSLR previews are usually already optimized
        rawBlob = await camera.capturePreviewAsBlob()
      }

      // Return RAW blob without any transformations
      // No rotation, no mirroring, no image processing
      // This preserves pixel-perfect accuracy needed for QR detection
      return rawBlob
    } catch (error) {
      console.error('QR preview capture failed:', error)
      // Don't disconnect camera on QR capture failure - it's not critical
      return undefined
    }
  }

  /**
   * Flush queued PTP events before/after a shutter capture. PTP drivers (notably Sony Alpha)
   * emit a per-shot ObjectAdded event; if the event/object handle from the previous shot is not
   * drained, the next captureImageAsFile downloads a stale handle whose RAM buffer was already
   * consumed and deleted → PTP 0x2009 "Invalid Object Handle" (this is exactly why the 2nd shutter
   * fails while the 1st succeeds). consumeEvents() drains the queue and reports whether more events
   * remain; loop bounded and break when empty. Best-effort: a transient busy error here must never
   * fail the capture, so we swallow it.
   */
  private drainCameraEvents = async (camera: Camera, maxEvents = 16): Promise<void> => {
    for (let i = 0; i < maxEvents; i++) {
      try {
        const hadEvent = await camera.consumeEvents()
        if (!hadEvent) return
      } catch (err) {
        console.warn('[CAPTURE] Failed to drain PTP camera events:', err)
        return
      }
    }
  }

  captureImageAsFile = async (cameraKey: CameraKey): Promise<File | undefined> => {
    if (this._isDisconnected[cameraKey]) return undefined
    const store = useCameraStore.getState()
    const isPrimary = cameraKey === 'primary'
    const deviceType = isPrimary ? store.deviceType : store.secondaryDeviceType
    const webcam = isPrimary ? store.webcam : store.secondaryWebcam
    const camera = isPrimary ? store.camera : store.secondaryCamera

    this._captureDepth[cameraKey] += 1
    try {
      if (deviceType === 'camera' && camera) {
        // Flush any stale ObjectAdded event left over from a prior shot (or liveview) so this
        // capture starts against a clean PTP event queue.
        await this.drainCameraEvents(camera)
        const file = await camera.captureImageAsFile()
        // Drain the ObjectAdded event this capture just generated so the *next* shutter starts clean.
        await this.drainCameraEvents(camera)
        return file
      } else if (deviceType === 'webcam' && webcam) {
        const settings = useSettingsStore.getState()
        const qualitySettings = isPrimary
          ? settings.cameraSettings.captureQuality
          : settings.secondaryCameraSettings.captureQuality
        return webcam.captureImageAsFile(
          qualitySettings?.maxWidth,
          qualitySettings?.maxHeight,
          qualitySettings?.quality
        )
      }
      return undefined
    } finally {
      this._captureDepth[cameraKey] = Math.max(0, this._captureDepth[cameraKey] - 1)
    }
  }

  captureImage = async (
    cameraKey: CameraKey = 'primary',
    filterFn?: FilterFn,
    frameRatio?: number,
    usePreview: boolean = true,
    onRawCaptured?: () => void
  ): Promise<File | undefined> => {
    if (this._isDisconnected[cameraKey]) return undefined
    this._captureDepth[cameraKey] += 1
    try {
      let imageBlob: Blob
      const isPrimary = cameraKey === 'primary'
      const settings = useSettingsStore.getState()
      const qualitySettings = isPrimary
        ? settings.cameraSettings.captureQuality
        : settings.secondaryCameraSettings.captureQuality
      // Bake the same rotation + mirror as live Preview (previewRotation), so stills, Filter, and
      // strip video match what the user saw. captureRotation remains available for hardware paths
      // that bypass the preview pipeline.
      const previewRotation = isPrimary
        ? settings.cameraSettings.previewRotation
        : settings.secondaryCameraSettings.previewRotation
      const mirror = isPrimary
        ? settings.cameraSettings.mirror
        : settings.secondaryCameraSettings.mirror

      if (usePreview) {
        // Fetch raw preview blob without any transforms — previewRotation + mirror applied below
        const rawBlob = await this.capturePreview(cameraKey, {
          maxWidth: qualitySettings?.maxWidth,
          maxHeight: qualitySettings?.maxHeight,
          quality: qualitySettings?.quality,
          rotation: 0,
          mirror: false
        })
        if (!rawBlob) return undefined
        imageBlob = rawBlob
      } else {
        const file = await this.captureImageAsFile(cameraKey)
        if (!file) return undefined
        markLastShutterCompleted(cameraKey)
        imageBlob = new Blob([await file.arrayBuffer()])
      }

      if (!imageBlob) return undefined

      // The raw frame is now in hand and the camera/shutter is free again. Signal the
      // caller so it can resume its countdown immediately — everything below (resize,
      // crop, filter, rotation) is pure CPU work that can finish in the background
      // without holding up the next shot.
      onRawCaptured?.()

      const currentCameraStore = useCameraStore.getState()
      const currentDeviceType = isPrimary
        ? currentCameraStore.deviceType
        : currentCameraStore.secondaryDeviceType
      let blobToProcess: Blob = imageBlob
      const camera = isPrimary ? currentCameraStore.camera : currentCameraStore.secondaryCamera

      if (currentDeviceType === 'camera' && camera) {
        blobToProcess = await imageUtils.resize(imageBlob, {
          maxWidth: qualitySettings?.maxWidth,
          maxHeight: qualitySettings?.maxHeight,
          quality: qualitySettings?.quality,
          resizeQuality: qualitySettings?.resizeQuality
        })
      }

      // Crop + filter in sensor space first (L2 frameRatio), then apply previewRotation + mirror.
      // Order is intentional: applying rotation *before* crop used to over-crop portrait
      // bitmaps when combined with 90°/270° and a landscape frame ratio (zoomed-in look).
      let processedBlob = await imageUtils.processPreview(blobToProcess, {
        ratio: frameRatio,
        filter: filterFn
      })

      if (previewRotation !== 0 || mirror) {
        processedBlob = await imageUtils.applyRotationMirroring(processedBlob, previewRotation, mirror)
      }

      // Legacy post-capture settle delay. Skipped when the caller manages pacing via
      // onRawCaptured (it has already resumed its countdown, so this would only add
      // dead time before the processed file is returned).
      if (!onRawCaptured) {
        await new Promise((resolve) => setTimeout(resolve, 1000))
      }

      return new File([processedBlob], 'dreambooth-image.png', { type: 'image/png' })
    } catch (error) {
      console.error('Image capture failed:', error)
      return undefined
    } finally {
      this._captureDepth[cameraKey] = Math.max(0, this._captureDepth[cameraKey] - 1)
    }
  }

  async disconnect(cameraKey: CameraKey = 'primary'): Promise<void> {
    const store = useCameraStore.getState()
    const isPrimary = cameraKey === 'primary'
    const camera = isPrimary ? store.camera : store.secondaryCamera
    const setCamera = isPrimary ? store.setCamera : store.setSecondaryCamera
    const setDeviceType = isPrimary ? store.setDeviceType : store.setSecondaryDeviceType

    try {
      if (camera) {
        this._isDisconnected[cameraKey] = true
        this._consecutivePreviewFailures[cameraKey] = 0
        await new Promise((resolve) => setTimeout(resolve, 1000))
        await camera.disconnect()
        setCamera(null)
        await new Promise((resolve) => setTimeout(resolve, 500))

        const devices = await navigator.usb.getDevices()
        for (const device of devices) {
          try {
            if (device.opened) {
              await device.releaseInterface(0)
              await device.close()
            }
          } catch (deviceErr) {
            console.warn('Error releasing USB device:', deviceErr)
          }
        }
      }
      setDeviceType(isPrimary ? 'webcam' : undefined)
    } catch (error: unknown) {
      console.error('Disconnect error:', error)
      cogoToast.error('Error during disconnect process')
    }
  }
}

export const unifiedCameraUtils = new UnifiedCameraUtils()
